import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { alertApi } from '../api/alertApi';
import { unlockAudioOnFirstInteraction } from '../utils/alertAudio';

const AlertContext = createContext();

export const useAlerts = () => {
  const context = useContext(AlertContext);
  if (!context) {
    throw new Error('useAlerts must be used within an AlertProvider');
  }
  return context;
};

// Default polling interval (1 minute)
const DEFAULT_POLLING_INTERVAL = 60000;

export const AlertProvider = ({ children }) => {
  const [alerts, setAlerts] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [recentAlerts, setRecentAlerts] = useState([]);
  const [alertsToShow, setAlertsToShow] = useState([]); // Alerts to show in modal when sound plays
  const [alertSettings, setAlertSettings] = useState({
    sound_enabled: true,
    active_hours_start: '00:00:00',
    active_hours_end: '23:59:59',
    alert_interval_minutes: 1,
  });
  const previousAlertIds = useRef(new Set());
  const isFirstLoad = useRef(true);
  const pollingIntervalRef = useRef(null);

  // Check if current time is within active hours - uses the EFFECTIVE (global +
  // this user's own override, see /alert-settings/me) window, not the raw global
  // one, so a personal active-hours override actually gates the in-app alarm.
  const isWithinActiveHours = useCallback(async () => {
    const { data, error } = await alertApi.getUserAlertSettings();
    let startDate = null;
    let endDate = null;
    if (data && !error) {
      setSoundEnabled(data.sound_enabled ?? true);
      startDate = data.active_hours_start;
      endDate = data.active_hours_end;
    }
    const now = new Date();
    const currentTime = now.toTimeString().slice(0, 8);

    const start = startDate || '00:00:00';
    const end = endDate || '23:59:59';

    if (start > end) {
      return currentTime >= start || currentTime <= end;
    }

    return currentTime >= start && currentTime <= end;
  }, [alertSettings.active_hours_start, alertSettings.active_hours_end]);

  // Load alert settings from API - the EFFECTIVE per-user settings (global +
  // this user's own override, /alert-settings/me), not the raw global-only
  // /alert-settings, so a personal "Re-notification interval" / active hours
  // override actually applies to this browser's own alarm/polling instead of
  // always following the admin's global values regardless of what this user set.
  const loadAlertSettings = useCallback(async () => {
    const { data, error } = await alertApi.getUserAlertSettings();
    if (data && !error) {
      setSoundEnabled(data.sound_enabled ?? true);
      setAlertSettings({
        sound_enabled: data.sound_enabled ?? true,
        active_hours_start: data.active_hours_start || '00:00:00',
        active_hours_end: data.active_hours_end || '23:59:59',
        alert_interval_minutes: data.alert_interval_minutes || 1,
      });
    }
  }, []);

  // Load alerts and check for new ones (loads first 300 for badge/new-alert detection —
  // must stay above the fleet's active-door count, or a truncated older alert can
  // "fall off" the page and reappear as new on a later poll, replaying the sound).
  // scope='today': the badge/alarm popup should only ever reflect today's alerts, not
  // the backend's multi-day default window - the full history lives on the /alerts page.
  const loadAlerts = useCallback(async (date = null, playSound = false) => {
    const { data, error } = await alertApi.getAlerts(date, 1, 300, null, null, null, false, 'today');

    if (data && !error) {
      const alertList = data.data ?? [];
      const total = data.pagination?.total ?? alertList.length;

      let newCriticalAlerts = [];
      if (playSound && !isFirstLoad.current) {
        const newAlerts = alertList.filter(alert => !previousAlertIds.current.has(alert.id));
        newCriticalAlerts = newAlerts.filter(a => a.severity === 'critical');

        if (newAlerts.length > 0) {
          //console.log("New alerts detected:", newAlerts.length);
          setRecentAlerts(prev => [...newAlerts, ...prev].slice(0, 5));
        }
      }

      if (playSound && await isWithinActiveHours() && newCriticalAlerts.length > 0) {
        setAlertsToShow(newCriticalAlerts);
      }

      if (playSound) {
        // Only the sound-checking pass gets to redefine the "already seen"
        // baseline — this is what the next poll diffs against to decide
        // whether to ring.
        previousAlertIds.current = new Set(alertList.map(a => a.id));
      } else {
        // Silent refresh (e.g. after acknowledging a single alert from the
        // Alerts page). Never add ids here: doing so previously marked
        // every currently-active alert as "seen" on every silent refresh,
        // so a genuinely new critical alert fetched in that same response
        // was swallowed before the next sound-checking poll ever got a
        // chance to diff it — the alarm would then never ring for it. Only
        // prune ids that are no longer present (resolved/acknowledged), so
        // they can be re-detected as new if they ever recur.
        const currentIds = new Set(alertList.map(a => a.id));
        previousAlertIds.current = new Set(
          [...previousAlertIds.current].filter(id => currentIds.has(id))
        );
      }
      isFirstLoad.current = false;

      setAlerts(alertList);
      setUnreadCount(total);
    }

    setLoading(false);
  }, [soundEnabled, isWithinActiveHours]);

  // Immediately show + ring the current critical alerts, bypassing the normal
  // "only sound on newly-seen ids" diffing — used when the user actively opens the
  // app from a push notification (closed-app "alarm clock" flow), where the point
  // is to reproduce the in-app alarm experience they'd have gotten had a tab
  // already been open, not to silently show the page like a routine visit.
  const ringNow = useCallback(async () => {
    const { data, error } = await alertApi.getAlerts(null, 1, 300, null, null, null, false, 'today');
    if (data && !error) {
      const alertList = data.data ?? [];
      // Keep the regular polling loop's "new alert" bookkeeping in sync, otherwise
      // the next scheduled poll would still find these ids absent from
      // previousAlertIds (only loadAlerts() normally updates it) and replay the
      // alarm for alerts the user was just shown via this same ringNow() call.
      previousAlertIds.current = new Set(alertList.map(a => a.id));

      const criticalAlerts = alertList.filter(a => a.severity === 'critical');
      if (criticalAlerts.length > 0) {
        setAlertsToShow(criticalAlerts);
      }
    }
  }, []);

  // Initial load
  useEffect(() => {
    loadAlertSettings();
    loadAlerts(null, true);
  }, []);

  // The automatic alarm (AlertModal, triggered by the polling timer below, with
  // no click involved) needs the shared AudioContext unlocked by a real user
  // gesture first, or the browser silently blocks it - see alertAudio.js.
  useEffect(() => unlockAudioOnFirstInteraction(), []);

  // Entry point for the closed-app "alarm clock" flow (see service-worker.js
  // notificationclick): a cold app open carries `?ring=1` in the URL (read once,
  // then stripped so a later refresh doesn't replay it); an already-open tab gets
  // the same signal via a postMessage from the service worker instead, since its
  // URL doesn't change.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('ring') === '1') {
      ringNow();
      params.delete('ring');
      const query = params.toString();
      window.history.replaceState(window.history.state, '', window.location.pathname + (query ? `?${query}` : '') + window.location.hash);
    }

    const handleServiceWorkerMessage = (event) => {
      if (event.data?.type === 'RING_ALERT') {
        ringNow();
      }
    };

    navigator.serviceWorker?.addEventListener('message', handleServiceWorkerMessage);
    return () => navigator.serviceWorker?.removeEventListener('message', handleServiceWorkerMessage);
  }, [ringNow]);

  // Polling for new alerts - uses interval from settings
  useEffect(() => {
    // Clear previous interval
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    // Calculate polling interval in milliseconds
    const intervalMs = (alertSettings.alert_interval_minutes || 1) * 60 * 1000;

    pollingIntervalRef.current = setInterval(() => {
      loadAlerts(null, true); // Check for new alerts and play sound
    }, intervalMs);

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [loadAlerts, alertSettings.alert_interval_minutes]);

  // Browsers throttle/suspend setInterval in background tabs, so a checkpoint
  // (18:00, 20:00, ...) reached while the tab was hidden could otherwise only be
  // detected once the throttled interval eventually fires. Re-checking immediately
  // when the tab regains focus closes that gap without changing the poll cadence.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !isFirstLoad.current) {
        loadAlerts(null, true);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [loadAlerts]);

  // Acknowledge alert - remove from list after acknowledging
  const acknowledgeAlert = useCallback(async (alertId, acknowledgedBy = 'User') => {
    const { data, error } = await alertApi.acknowledgeAlert(alertId, acknowledgedBy);

    if (data && !error) {
      // Remove the alert from the list
      setAlerts(prev => prev.filter(a => a.id !== alertId));
      setUnreadCount(prev => Math.max(0, prev - 1));
    }

    return { data, error };
  }, []);

  // Resolve alert
  const resolveAlert = useCallback(async (alertId, notes = '') => {
    const { data, error } = await alertApi.resolveAlert(alertId, notes);

    if (data && !error) {
      const now = new Date().toISOString();
      setAlerts(prev =>
        prev.map(a => a.id === alertId ? {
          ...a,
          resolved: true,
          resolvedAt: now,
          resolvedNotes: notes
        } : a)
      );
    }

    return { data, error };
  }, []);
  
  // Mark all as read
  const markAllAsRead = useCallback(async () => {
    const unacknowledgedAlerts = alerts.filter(a => !a.acknowledged);
    
    for (const alert of unacknowledgedAlerts) {
      await acknowledgeAlert(alert.id);
    }
    
    setUnreadCount(0);
  }, [alerts, acknowledgeAlert]);
  
  // Clear recent alerts (dismiss popups)
  const clearRecentAlerts = useCallback(() => {
    setRecentAlerts([]);
  }, []);
  
  // Dismiss specific recent alert
  const dismissRecentAlert = useCallback((alertId) => {
    setRecentAlerts(prev => prev.filter(a => a.id !== alertId));
  }, []);

  // Clear the alerts to show in modal
  const clearAlertsToShow = useCallback(() => {
    setAlertsToShow([]);
  }, []);
  
  const value = {
    alerts,
    unreadCount,
    loading,
    recentAlerts,
    alertsToShow,
    soundEnabled,
    setSoundEnabled,
    alertSettings,
    acknowledgeAlert,
    resolveAlert,
    markAllAsRead,
    clearRecentAlerts,
    dismissRecentAlert,
    clearAlertsToShow,
    refreshAlerts: loadAlerts,
    refreshSettings: loadAlertSettings,
    ringNow,
  };
  
  return (
    <AlertContext.Provider value={value}>
      {children}
    </AlertContext.Provider>
  );
};

